/* Olilu core: shared helpers used by every section script.
   Olilu.money(cents)            '£24' for whole pounds, '£21.60' otherwise
   Olilu.cart.get/add/change/update -> Promise<cart>; every change fires document 'olilu:cart' {detail: {cart, added}}
   Olilu.basket.open()           fires 'olilu:basket-open' (the basket drawer listens); falls back to /cart
   Olilu.drawer.open(el, opener) / close(el)   any .drawer element, with scrim, scroll lock, focus trap and Esc
   Olilu.delivery()              dispatch and arrival dates from the cut-off, skipping weekends and UK bank holidays
   Olilu.reviews.load(handle)    reviews-<handle>.json from theme assets ('shop' for the whole shop)
   Olilu.reviews.card(row, opts) / hist(hist, opts) / stars(n)   HTML strings
   Olilu.ready(selector, fn)     runs fn(el) for each match now and after theme-editor section reloads */
(function () {
  'use strict';
  var O = window.Olilu = window.Olilu || {};
  var cfg = O.cfg = window.oliluConfig || {};
  var $ = O.$ = function (s, root) { return (root || document).querySelector(s); };
  var $$ = O.$$ = function (s, root) { return Array.prototype.slice.call((root || document).querySelectorAll(s)); };

  O.esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  };

  O.money = function (cents) {
    var n = Math.round(Number(cents) || 0);
    var pounds = Math.floor(n / 100), pence = n % 100;
    var whole = String(pounds).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '£' + whole + (pence ? '.' + String(pence).padStart(2, '0') : '');
  };

  /* ---------- Init hooks (also re-run when a section reloads in the theme editor) ---------- */
  var inits = [];
  O.ready = function (selector, fn) {
    inits.push([selector, fn]);
    run(document, selector, fn);
  };
  function run(root, selector, fn) {
    $$(selector, root).forEach(function (el) {
      if (el.__olilu && el.__olilu.indexOf(selector) > -1) return;
      (el.__olilu = el.__olilu || []).push(selector);
      try { fn(el); } catch (e) { if (window.console) console.error(e); }
    });
    if (root !== document && root.matches && root.matches(selector) && !(root.__olilu && root.__olilu.indexOf(selector) > -1)) {
      (root.__olilu = root.__olilu || []).push(selector);
      fn(root);
    }
  }
  O.init = function (root) { inits.forEach(function (i) { run(root || document, i[0], i[1]); }); };
  document.addEventListener('shopify:section:load', function (e) { O.init(e.target); });

  /* ---------- Fetch helpers ---------- */
  var jsonCache = {};
  O.json = function (url) {
    if (!jsonCache[url]) {
      jsonCache[url] = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      });
      jsonCache[url].catch(function () { delete jsonCache[url]; });
    }
    return jsonCache[url];
  };
  // Section Rendering API: returns the inner HTML of a section rendered for `url`
  O.section = function (url, sectionId) {
    var u = new URL(url, location.origin);
    u.searchParams.set('section_id', sectionId);
    return fetch(u.toString(), { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      return r.text();
    });
  };
  O.parse = function (html) { return new DOMParser().parseFromString(html, 'text/html'); };

  /* ---------- Cart ---------- */
  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) { var err = new Error(j.description || j.message || 'Something went wrong'); err.data = j; throw err; }
        return j;
      });
    });
  }
  function emit(cart, added) {
    O.cart.last = cart;
    document.dispatchEvent(new CustomEvent('olilu:cart', { detail: { cart: cart, added: added || null } }));
    return cart;
  }
  var routes = cfg.routes || {};
  O.cart = {
    last: null,
    get: function () {
      return fetch((routes.cart || '/cart') + '.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
    },
    // items: [{id, quantity, properties}] (identical variant ids are merged into one line first)
    add: function (items) {
      var merged = [];
      (Array.isArray(items) ? items : [items]).forEach(function (it) {
        var id = Number(it.id), q = Number(it.quantity || 1);
        var hit = !it.properties && merged.filter(function (m) { return m.id === id && !m.properties; })[0];
        if (hit) hit.quantity += q; else merged.push({ id: id, quantity: q, properties: it.properties });
      });
      return post((routes.cartAdd || '/cart/add') + '.js', { items: merged }).then(function (res) {
        return O.cart.get().then(function (cart) { return emit(cart, res.items || [res]); });
      });
    },
    change: function (key, quantity) {
      return post((routes.cartChange || '/cart/change') + '.js', { id: String(key), quantity: quantity }).then(function (cart) { return emit(cart); });
    },
    update: function (body) {
      return post((routes.cartUpdate || '/cart/update') + '.js', body).then(function (cart) { return emit(cart); });
    }
  };

  O.basket = {
    open: function (opener) {
      if (!document.getElementById('BasketDrawer')) { location.href = routes.cart || '/cart'; return; }
      document.dispatchEvent(new CustomEvent('olilu:basket-open', { detail: { opener: opener || document.activeElement } }));
    }
  };
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-basket-open]');
    if (t && document.getElementById('BasketDrawer')) { e.preventDefault(); O.basket.open(t); }
  });

  /* ---------- Drawers ---------- */
  var scrim, openStack = [];
  function getScrim() {
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.className = 'scrim';
      scrim.hidden = true;
      scrim.addEventListener('click', function () { if (openStack.length) O.drawer.close(openStack[openStack.length - 1]); });
      document.body.appendChild(scrim);
    }
    return scrim;
  }
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  O.focusables = function (el) { return $$(FOCUSABLE, el).filter(function (n) { return n.offsetParent !== null || n === document.activeElement; }); };
  O.lock = function (on) { document.body.classList.toggle('is-locked', !!on); };
  O.drawer = {
    open: function (el, opener) {
      if (!el || el.classList.contains('is-open')) return;
      if (el.parentNode !== document.body) document.body.appendChild(el);
      el.__opener = opener || document.activeElement;
      var s = getScrim();
      s.hidden = false;
      el.hidden = false;
      el.setAttribute('role', el.getAttribute('role') || 'dialog');
      el.setAttribute('aria-modal', 'true');
      requestAnimationFrame(function () {
        s.classList.add('is-open');
        el.classList.add('is-open');
        var f = $('[data-autofocus]', el) || O.focusables(el)[0];
        if (f) setTimeout(function () { f.focus({ preventScroll: true }); }, 60);
      });
      openStack.push(el);
      O.lock(true);
      $$('[aria-controls="' + el.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      el.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    },
    close: function (el) {
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      openStack = openStack.filter(function (d) { return d !== el; });
      if (!openStack.length) {
        var s = getScrim();
        s.classList.remove('is-open');
        setTimeout(function () { if (!openStack.length) s.hidden = true; }, 320);
        O.lock(false);
      }
      setTimeout(function () { if (!el.classList.contains('is-open')) el.hidden = true; }, 320);
      $$('[aria-controls="' + el.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (el.__opener && el.__opener.focus) el.__opener.focus({ preventScroll: true });
      el.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  };
  document.addEventListener('click', function (e) {
    var o = e.target.closest('[data-drawer-open]');
    if (o) {
      var d = document.getElementById(o.getAttribute('data-drawer-open'));
      if (d) { e.preventDefault(); O.drawer.open(d, o); }
      return;
    }
    var c = e.target.closest('[data-drawer-close]');
    if (c) { e.preventDefault(); O.drawer.close(c.closest('.drawer')); }
  });
  document.addEventListener('keydown', function (e) {
    var top = openStack[openStack.length - 1];
    if (!top) return;
    if (e.key === 'Escape') { O.drawer.close(top); return; }
    if (e.key === 'Tab') {
      var f = O.focusables(top);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Rails: prev/next buttons for [data-rail] ---------- */
  O.ready('[data-rail-wrap]', function (wrap) {
    var rail = $('[data-rail]', wrap);
    var prev = $('[data-rail-prev]', wrap), next = $('[data-rail-next]', wrap);
    if (!rail) return;
    function step() { var item = rail.firstElementChild; return item ? item.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0) : rail.clientWidth; }
    function update() {
      var max = rail.scrollWidth - rail.clientWidth - 2;
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max;
      var segs = $$('[data-rail-segments] span', wrap);
      if (segs.length) {
        var i = Math.round(rail.scrollLeft / step());
        segs.forEach(function (s, n) { s.classList.toggle('is-active', n === Math.min(i, segs.length - 1)); });
      }
    }
    if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step() * Math.max(1, Math.floor(rail.clientWidth / step())), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step() * Math.max(1, Math.floor(rail.clientWidth / step())), behavior: 'smooth' }); });
    rail.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    update();
  });

  /* ---------- Delivery dates (Europe/London) ---------- */
  var HOLIDAYS = ['2026-12-25', '2026-12-28', '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28'];
  function londonNow() {
    var p = {};
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
        .formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
      return { d: new Date(+p.year, +p.month - 1, +p.day), h: +p.hour % 24, m: +p.minute };
    } catch (e) {
      var n = new Date();
      return { d: new Date(n.getFullYear(), n.getMonth(), n.getDate()), h: n.getHours(), m: n.getMinutes() };
    }
  }
  function iso(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
  function workDay(d) { return d.getDay() !== 0 && d.getDay() !== 6 && HOLIDAYS.indexOf(iso(d)) === -1; }
  function addWork(d, n) { var x = new Date(d); while (n > 0) { x.setDate(x.getDate() + 1); if (workDay(x)) n--; } return x; }
  O.day = function (d) { return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); };
  O.delivery = function () {
    var cut = Number(cfg.cutoff) || 13, days = Number(cfg.ukDays) || 2;
    var now = londonNow();
    var sameDay = workDay(now.d) && now.h < cut;
    var dispatch = sameDay ? now.d : addWork(now.d, 1);
    var from = addWork(dispatch, days), to = addWork(from, 1);
    var x = cfg.xmas || {};
    var t = iso(now.d);
    var inXmas = !!(x.orderBy && x.start && t >= x.start && t <= x.orderBy);
    return {
      today: now.d, sameDay: sameDay, dispatch: dispatch, from: from, to: to,
      range: O.day(from) + ' – ' + O.day(to),
      xmas: inXmas, xmasOrderBy: x.orderBy ? new Date(x.orderBy + 'T12:00:00') : null
    };
  };
  O.ready('[data-delivery-range]', function (el) { el.textContent = O.delivery().range; });

  /* ---------- Reviews ---------- */
  // Rows: [rating, text, name, 'Oct 2026', [photo urls], shop reply, product title?, product handle?]
  var R = O.reviews = {};
  R.load = function (handle) { return O.json((cfg.assets || '/') + 'reviews-' + handle + '.json'); };
  // Featured spots (home letters, the product page's newest-review line, ad reviews) use "newest with text", skipping reviews
  // that mention a medical condition: UK advertising rules treat a testimonial we feature as our own claim. Every review,
  // including these, still shows in the full lists.
  var HEALTH = /\b(fibromyalg\w*|arthriti\w*|rheumat\w*|raynaud\w*|chilblain\w*|circulation|eczema|psoriasis|dermatitis|allerg\w*|chemo\w*|cancer|diabet\w*|lupus|multiple sclerosis|neuropath\w*|carpal|tendon\w*|joints?|pain\w*|aches?|aching|doctor|hospital|medical|medication|symptoms?)\b/i;
  R.featurable = function (r) { return !!(r && r[1]) && !HEALTH.test(r[1]); };
  R.stars = function (n, cls) {
    var v = Math.max(0, Math.min(5, Number(n) || 0));
    return '<span class="stars ' + (cls || '') + '" style="--pct:' + (v * 20) + '%" role="img" aria-label="' + (Math.round(v * 10) / 10) + ' out of 5 stars"></span>';
  };
  R.hist = function (hist, opts) {
    opts = opts || {};
    var total = (hist || []).reduce(function (a, b) { return a + b; }, 0) || 1;
    return '<div class="hist">' + (hist || []).map(function (c, i) {
      var star = 5 - i, tag = opts.buttons ? 'button type="button" data-star="' + star + '" aria-pressed="false"' : 'div';
      return '<' + tag + ' class="hist__row"><span>' + star + ' star</span><span class="hist__bar"><span style="width:' + (c / total * 100).toFixed(1) + '%"></span></span><span>' + c.toLocaleString('en-GB') + '</span></' + (opts.buttons ? 'button' : 'div') + '>';
    }).join('') + '</div>';
  };
  R.card = function (r, opts) {
    opts = opts || {};
    var text = r[1] || '', photos = r[4] || [], reply = r[5] || '';
    var long = text.length > 320;
    var h = '<article class="letter' + (opts.cls ? ' ' + opts.cls : '') + '">';
    h += R.stars(r[0]);
    if (text) h += '<p class="letter__quote' + (long ? ' is-clamped' : '') + '">' + O.esc(text) + '</p>';
    if (long) h += '<button type="button" class="text-cta letter__more" data-review-more>Read more</button>';
    if (photos.length && opts.photos !== false) {
      h += '<div class="letter__photos">' + photos.slice(0, 4).map(function (u) {
        return '<a href="' + O.esc(u) + '" target="_blank" rel="noopener" aria-label="Customer photo, opens full size"><img src="' + O.esc(u.replace(/\.jpg(\?|$)/, '_200x200.jpg$1')) + '" alt="Photo from ' + O.esc(r[2]) + '" loading="lazy" width="72" height="72"></a>';
      }).join('') + '</div>';
    }
    h += '<p class="letter__meta"><strong>' + O.esc(r[2] || 'Etsy buyer') + '</strong> · ' + O.esc(r[3] || '') + ' · Etsy review</p>';
    if (opts.product !== false && r[6]) {
      h += '<p class="letter__product">On ' + (r[7] ? '<a href="/products/' + O.esc(r[7]) + '">' + O.esc(r[6]) + '</a>' : O.esc(r[6])) + '</p>';
    }
    if (reply) h += '<div class="letter__reply"><strong>Reply from Olilu</strong>' + O.esc(reply) + '</div>';
    return h + '</article>';
  };
  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-review-more]');
    if (!b) return;
    var q = b.parentNode.querySelector('.letter__quote');
    if (q) q.classList.remove('is-clamped');
    b.remove();
  });

  /* ---------- Misc ---------- */
  O.debounce = function (fn, ms) { var t; return function () { var a = arguments, s = this; clearTimeout(t); t = setTimeout(function () { fn.apply(s, a); }, ms || 200); }; };
  O.inView = function (el, fn, margin) {
    if (!('IntersectionObserver' in window)) { fn(); return; }
    var io = new IntersectionObserver(function (es) { if (es[0].isIntersecting) { io.disconnect(); fn(); } }, { rootMargin: margin || '300px' });
    io.observe(el);
  };
})();
